'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

function isHeadlessBrowser(): boolean {
  const nav = navigator as any;
  // Selenium / WebDriver
  if (nav.webdriver) return true;
  // PhantomJS
  if ((window as any).callPhantom || (window as any)._phantom) return true;
  // HeadlessChrome in UA
  if (/HeadlessChrome/i.test(navigator.userAgent)) return true;
  // NOTE: do NOT use `navigator.plugins.length === 0` as a headless signal.
  // Real mobile browsers (Chrome/Safari/Samsung Internet on phones) report 0
  // plugins, so that check false-positived on virtually every phone and blanked
  // the whole page (document.documentElement.innerHTML = '') — the mobile
  // white-screen bug. Removed intentionally.
  // Puppeteer/Playwright marker
  if ((window as any).__playwright || (window as any).__pw_manual) return true;
  return false;
}

export default function ContentProtection() {
  const pathname = usePathname();

  useEffect(() => {
    // The whole admin panel needs normal copy/paste/select-all to work —
    // editing blog posts, tables, forms, etc. Protection is only meant for
    // public-facing content pages, so skip it entirely here.
    if (pathname?.startsWith('/admin')) return;

    // Blank the page if a headless browser / scraper is detected
    if (isHeadlessBrowser()) {
      document.documentElement.innerHTML = '';
      return;
    }

    const prevent = (e: Event) => e.preventDefault();

    const handleKeydown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;

      // Block copy, select-all, save, view-source, print
      if (ctrl && ['c', 'a', 's', 'u', 'p'].includes(key)) {
        e.preventDefault();
        return;
      }
      // Block devtools shortcuts
      if (ctrl && e.shiftKey && ['i', 'j', 'c', 'k'].includes(key)) {
        e.preventDefault();
        return;
      }
      // Block F12, PrintScreen
      if (e.key === 'F12' || e.key === 'PrintScreen') {
        e.preventDefault();
        return;
      }
    };

    document.addEventListener('contextmenu', prevent);
    document.addEventListener('copy', prevent);
    document.addEventListener('cut', prevent);
    document.addEventListener('selectstart', prevent);
    document.addEventListener('dragstart', prevent);
    document.addEventListener('keydown', handleKeydown, true);

    // Block all images from being dragged or right-clicked
    const blockImg = () => {
      document.querySelectorAll('img').forEach((img) => {
        img.setAttribute('draggable', 'false');
        img.addEventListener('contextmenu', prevent);
      });
    };
    blockImg();

    // Re-apply for dynamically added images
    const observer = new MutationObserver(blockImg);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener('contextmenu', prevent);
      document.removeEventListener('copy', prevent);
      document.removeEventListener('cut', prevent);
      document.removeEventListener('selectstart', prevent);
      document.removeEventListener('dragstart', prevent);
      document.removeEventListener('keydown', handleKeydown, true);
      observer.disconnect();
    };
  }, [pathname]);

  return null;
}
